/**
 * Ad Accounts page. Lists ad accounts (optionally scoped to a linked Facebook
 * account), supports a name search filter, and lets the user trigger a sync
 * from the Facebook Graph API for the selected Facebook account.
 */
import { useCallback, useEffect, useMemo, useState, type FormEvent, type ReactElement } from 'react';
import Card from '@/components/Cards/Card';
import Table, { type Column } from '@/components/Tables/Table';
import Button from '@/components/Forms/Button';
import Input from '@/components/Forms/Input';
import Select, { type SelectOption } from '@/components/Forms/Select';
import MultiSelect from '@/components/Forms/MultiSelect';
import Modal from '@/components/Modals/Modal';
import Badge from '@/components/common/Badge';
import Alert from '@/components/Alerts/Alert';
import { useToast } from '@/components/Alerts/toastContext';
import * as facebookAccountService from '@/services/facebookAccountService';
import * as adAccountService from '@/services/adAccountService';
import * as campaignService from '@/services/campaignService';
import type { AdAccountDTO, CampaignDTO, FacebookAccountDTO } from '@shared/types';
import { adAccountNumber, adsManagerUrl, formatCurrency, formatDate, paymentSettingsUrl } from '@/utils/formatters';
import { entityStatusMeta } from '@/pages/labels';

/** Ad accounts management view. */
export default function AdAccounts(): ReactElement {
  const toast = useToast();

  const [fbAccounts, setFbAccounts] = useState<FacebookAccountDTO[]>([]);
  const [adAccounts, setAdAccounts] = useState<AdAccountDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [selectedFbId, setSelectedFbId] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [syncingBilling, setSyncingBilling] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  // Spend-limit modal state.
  const [capTarget, setCapTarget] = useState<AdAccountDTO | null>(null);
  const [capValue, setCapValue] = useState('');
  const [savingCap, setSavingCap] = useState(false);

  // Bulk selection + bulk spend-limit.
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [bulkCapValue, setBulkCapValue] = useState('');
  const [bulkingCap, setBulkingCap] = useState(false);

  // Deploy-from-template ("Tạo chiến dịch từ mẫu") modal state. Creating a
  // campaign lives here because this is where ad accounts are listed & checkable.
  const [deployOpen, setDeployOpen] = useState(false);
  const [templates, setTemplates] = useState<CampaignDTO[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [deployTemplateId, setDeployTemplateId] = useState('');
  const [deployAccounts, setDeployAccounts] = useState<string[]>([]);
  const [deployBudget, setDeployBudget] = useState('');
  const [deployCopies, setDeployCopies] = useState('1');
  const [deployAgeMin, setDeployAgeMin] = useState('');
  const [deployAgeMax, setDeployAgeMax] = useState('');
  const [deployName, setDeployName] = useState('');
  const [deployActivate, setDeployActivate] = useState(true);
  const [deploying, setDeploying] = useState(false);

  // Load the list of Facebook accounts once (for the filter + sync target).
  useEffect(() => {
    facebookAccountService
      .list()
      .then(setFbAccounts)
      .catch((err: unknown) => {
        toast.error(err instanceof Error ? err.message : 'Không thể tải tài khoản Facebook');
      });
  }, [toast]);

  const loadAdAccounts = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    setSelectedIds([]);
    try {
      const data = await adAccountService.list(selectedFbId || undefined);
      setAdAccounts(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Không thể tải tài khoản quảng cáo');
    } finally {
      setLoading(false);
    }
  }, [selectedFbId]);

  useEffect(() => {
    void loadAdAccounts();
  }, [loadAdAccounts]);

  const handleSync = async (): Promise<void> => {
    if (!selectedFbId) {
      toast.warning('Vui lòng chọn một tài khoản Facebook để đồng bộ');
      return;
    }
    setSyncing(true);
    try {
      const result = await adAccountService.sync(selectedFbId);
      toast.success(result.message ?? `Đã đồng bộ ${result.synced} tài khoản quảng cáo`);
      await loadAdAccounts();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Đồng bộ thất bại');
    } finally {
      setSyncing(false);
    }
  };

  // ONE button to refresh everything (revenue + accounts + campaigns + insights),
  // then hard-reload so every number is fresh (no stale illusion).
  const handleRefreshAll = async (): Promise<void> => {
    setRefreshing(true);
    try {
      const r = await campaignService.refreshAll();
      toast.success(r.message || 'Đã làm mới tất cả');
      window.location.reload();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Làm mới thất bại');
      setRefreshing(false);
    }
  };

  const openCap = (account: AdAccountDTO): void => {
    setCapTarget(account);
    setCapValue(account.spendCap && Number(account.spendCap) > 0 ? account.spendCap : '');
  };

  const handleSaveCap = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (!capTarget) return;
    const v = capValue.trim() === '' ? 0 : Number(capValue);
    if (!Number.isFinite(v) || v < 0) {
      toast.warning('Giới hạn không hợp lệ (để trống = bỏ giới hạn)');
      return;
    }
    setSavingCap(true);
    try {
      const updated = await adAccountService.setSpendCap(capTarget.id, v);
      setAdAccounts((prev) => prev.map((a) => (a.id === updated.id ? updated : a)));
      toast.success(v === 0 ? 'Đã bỏ giới hạn chi tiêu' : `Đã đặt giới hạn ${formatCurrency(v)}`);
      setCapTarget(null);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Đặt giới hạn thất bại');
    } finally {
      setSavingCap(false);
    }
  };

  const handleBulkCap = async (): Promise<void> => {
    if (selectedIds.length === 0) return;
    const v = bulkCapValue.trim() === '' ? 0 : Number(bulkCapValue);
    if (!Number.isFinite(v) || v < 0) {
      toast.warning('Giới hạn không hợp lệ (để trống = bỏ giới hạn)');
      return;
    }
    setBulkingCap(true);
    try {
      const r = await adAccountService.bulkSpendCap(selectedIds, v);
      toast.success(r.message);
      const failed = r.results.filter((x) => !x.ok);
      if (failed.length > 0) {
        toast.error(`${failed.length} lỗi: ` + failed.slice(0, 2).map((f) => f.error ?? '').join(' • '));
      }
      setBulkCapValue('');
      await loadAdAccounts();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Đặt giới hạn hàng loạt thất bại');
    } finally {
      setBulkingCap(false);
    }
  };

  const openDeployModal = async (): Promise<void> => {
    setDeployOpen(true);
    setDeployTemplateId('');
    // Pre-select the accounts already checked in the table (if any).
    setDeployAccounts(selectedIds.length > 0 ? [...selectedIds] : []);
    setDeployBudget('50000');
    setDeployCopies('1');
    setDeployAgeMin('');
    setDeployAgeMax('');
    setDeployName('');
    setDeployActivate(true);
    setLoadingTemplates(true);
    try {
      const res = await campaignService.list({ template: true });
      setTemplates(res.campaigns);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Không thể tải danh sách mẫu');
    } finally {
      setLoadingTemplates(false);
    }
  };

  // When a template is picked, prefill name/budget from it.
  const onPickTemplate = (id: string): void => {
    setDeployTemplateId(id);
    const t = templates.find((c) => c.id === id);
    if (t) {
      setDeployName(t.name ?? '');
      setDeployBudget(t.dailyBudget ?? '50000');
    }
  };

  const handleDeploy = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (!deployTemplateId) {
      toast.warning('Vui lòng chọn mẫu');
      return;
    }
    if (deployAccounts.length === 0) {
      toast.warning('Vui lòng chọn ít nhất 1 tài khoản đích');
      return;
    }
    const budget = Number(deployBudget);
    if (!Number.isFinite(budget) || budget <= 0) {
      toast.warning('Ngân sách/ngày không hợp lệ');
      return;
    }
    const copies = Math.min(20, Math.max(1, Math.floor(Number(deployCopies) || 1)));
    setDeploying(true);
    try {
      const result = await campaignService.deployFromTemplate({
        templateCampaignId: deployTemplateId,
        targetAdAccountIds: deployAccounts,
        dailyBudget: budget,
        copies,
        ageMin: deployAgeMin ? Number(deployAgeMin) : undefined,
        ageMax: deployAgeMax ? Number(deployAgeMax) : undefined,
        name: deployName.trim() || undefined,
        activate: deployActivate,
      });
      toast.success(result.message);
      const failed = result.results.filter((r) => !r.ok);
      if (failed.length > 0) {
        toast.error(
          `Lỗi ${failed.length} tài khoản: ` +
            failed.slice(0, 2).map((f) => `${f.targetAdAccountName ?? ''}: ${f.error ?? ''}`).join(' • '),
        );
      }
      const warns = result.results.flatMap((r) => r.warnings);
      if (warns.length > 0) toast.warning(warns.slice(0, 3).join(' • '));
      setDeployOpen(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Tạo chiến dịch từ mẫu thất bại');
    } finally {
      setDeploying(false);
    }
  };

  const fbOptions: SelectOption[] = useMemo(
    () =>
      fbAccounts.map((a) => ({
        value: a.id,
        label: a.name ? `${a.name} · ${a.businessManagerId}` : `BM ${a.businessManagerId}`,
      })),
    [fbAccounts],
  );

  // Target-account picker options (BM-scoped list; covers all checked rows).
  const adAccountOptions: SelectOption[] = useMemo(
    () => adAccounts.map((a) => ({ value: a.id, label: a.name ?? adAccountNumber(a.adAccountIdFb) })),
    [adAccounts],
  );

  // Distinct currencies among the selected deploy targets — budget is entered in
  // each account's own currency, so mixing currencies in one deploy is risky.
  const deployCurrencies = useMemo(() => {
    const set = new Set<string>();
    for (const id of deployAccounts) {
      const a = adAccounts.find((x) => x.id === id);
      if (a?.currency) set.add(a.currency.toUpperCase());
    }
    return [...set];
  }, [deployAccounts, adAccounts]);

  // Template picker options: show name + route (parsed from name) or source account.
  const templateOptions: SelectOption[] = useMemo(
    () =>
      templates.map((t) => {
        const m = (t.name ?? '').toLowerCase().match(/\/([a-z0-9]+)/);
        const suffix = m ? `/${m[1]}` : (t.adAccountName ?? t.adAccountIdFb ?? '');
        return { value: t.id, label: `${t.name ?? '(không tên)'}${suffix ? ` · ${suffix}` : ''}` };
      }),
    [templates],
  );

  const statusOptions: SelectOption[] = useMemo(() => {
    const set = new Set(adAccounts.map((a) => a.status).filter((s): s is string => Boolean(s)));
    return [...set].sort().map((s) => ({ value: s, label: entityStatusMeta(s).label }));
  }, [adAccounts]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return adAccounts.filter((a) => {
      if (statusFilter && (a.status ?? '') !== statusFilter) return false;
      if (q && !((a.name ?? '').toLowerCase().includes(q) || a.adAccountIdFb.toLowerCase().includes(q))) {
        return false;
      }
      return true;
    });
  }, [adAccounts, search, statusFilter]);

  const columns: Column<AdAccountDTO>[] = [
    {
      key: 'adAccountIdFb',
      header: 'ID tài khoản',
      render: (row) => (
        <a
          href={adsManagerUrl(row.adAccountIdFb)}
          target="_blank"
          rel="noopener noreferrer"
          className="font-medium text-brand-600 hover:underline"
          title="Mở trong Trình quản lý quảng cáo"
        >
          {adAccountNumber(row.adAccountIdFb)}
        </a>
      ),
    },
    {
      key: 'name',
      header: 'Tên',
      render: (row) => row.name ?? '—',
    },
    {
      key: 'ownerName',
      header: 'Chủ sở hữu',
      sortAccessor: (row) => (row.ownerName ?? '').toLowerCase(),
      render: (row) => row.ownerName ?? <span className="text-gray-400">—</span>,
    },
    {
      key: 'currency',
      header: 'Tiền tệ',
      render: (row) => row.currency ?? '—',
    },
    {
      key: 'fundingSource',
      header: 'Đuôi thẻ',
      // Sortable: no-card accounts ("0" prefix) group to the TOP when ascending,
      // so it's easy to find which accounts still need a card.
      sortAccessor: (row) => (row.fundingSource ? `1${row.fundingSource.toLowerCase()}` : '0'),
      defaultSortDir: 'asc',
      render: (row) =>
        row.fundingSource ? (
          <span className="text-gray-900">{row.fundingSource}</span>
        ) : (
          <span className="font-medium text-red-600">Chưa có thẻ</span>
        ),
    },
    {
      key: 'amountSpent',
      header: 'Tổng tiêu',
      align: 'right',
      sortAccessor: (row) => (row.amountSpent != null ? Number(row.amountSpent) : null),
      defaultSortDir: 'desc',
      render: (row) => (row.amountSpent != null ? formatCurrency(row.amountSpent) : <span className="text-gray-400">—</span>),
    },
    {
      key: 'spendCap',
      header: 'Giới hạn',
      align: 'right',
      render: (row) =>
        row.spendCap != null && Number(row.spendCap) > 0 ? (
          formatCurrency(row.spendCap)
        ) : (
          <span className="text-gray-400">Không</span>
        ),
    },
    {
      key: 'status',
      header: 'Trạng thái',
      render: (row) => {
        const meta = entityStatusMeta(row.status);
        return <Badge color={meta.color}>{meta.label}</Badge>;
      },
    },
    {
      key: 'actions',
      header: 'Hành động',
      align: 'right',
      render: (row) => (
        <div className="flex items-center justify-end gap-2">
          <a
            href={paymentSettingsUrl(row.adAccountIdFb)}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center rounded-md border border-brand-200 bg-brand-50 px-3 py-1.5 text-sm font-medium text-brand-700 hover:bg-brand-100"
            title="Mở trang thanh toán Facebook để thêm/verify thẻ"
          >
            {row.fundingSource ? 'Quản lý thẻ' : 'Thêm thẻ'}
          </a>
          <Button variant="secondary" onClick={() => openCap(row)}>
            Đặt giới hạn
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Tài khoản quảng cáo</h1>
          <p className="mt-1 text-sm text-gray-500">
            Danh sách tài khoản quảng cáo đã đồng bộ từ Facebook.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => void openDeployModal()}>Tạo chiến dịch</Button>
          <Button variant="secondary" loading={refreshing} onClick={() => void handleRefreshAll()}>
            🔄 Làm mới
          </Button>
        </div>
      </div>

      {error && <Alert type="error">{error}</Alert>}

      <Card>
        <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
          <Select
            label="Tài khoản doanh nghiệp (BM)"
            options={fbOptions}
            placeholder="Tất cả BM"
            value={selectedFbId}
            onChange={(e) => setSelectedFbId(e.target.value)}
          />
          <Select
            label="Trạng thái"
            options={statusOptions}
            placeholder="Tất cả trạng thái"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          />
          <Input
            label="Tìm kiếm"
            placeholder="Tìm theo tên hoặc ID..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {selectedIds.length > 0 && (
          <div className="mb-3 flex flex-wrap items-center gap-2 rounded-md border border-brand-200 bg-brand-50 p-3">
            <span className="text-sm font-semibold text-brand-800">{selectedIds.length} tài khoản đã chọn</span>
            <input
              type="number"
              min={0}
              value={bulkCapValue}
              onChange={(e) => setBulkCapValue(e.target.value)}
              placeholder="Giới hạn (VND) — trống = bỏ"
              className="input w-48"
            />
            <Button variant="secondary" loading={bulkingCap} onClick={() => void handleBulkCap()}>
              Đặt giới hạn hàng loạt
            </Button>
            <Button onClick={() => void openDeployModal()}>
              Tạo chiến dịch cho TK đã chọn
            </Button>
            <span className="text-xs text-gray-500">Áp cho tất cả tài khoản đã chọn.</span>
            <button type="button" className="ml-auto text-sm text-gray-500 underline" onClick={() => setSelectedIds([])}>
              Bỏ chọn
            </button>
          </div>
        )}

        <Table
          columns={columns}
          rows={filtered}
          loading={loading}
          rowKey={(row) => row.id}
          selectable
          selected={selectedIds}
          onSelectedChange={setSelectedIds}
          emptyMessage="Chưa có tài khoản quảng cáo. Hãy chọn tài khoản BM và nhấn Đồng bộ."
        />
      </Card>

      {/* Spend-limit modal */}
      <Modal
        isOpen={capTarget !== null}
        onClose={() => setCapTarget(null)}
        title="Đặt giới hạn chi tiêu tài khoản"
        footer={
          <>
            <Button variant="secondary" onClick={() => setCapTarget(null)}>
              Hủy
            </Button>
            <Button type="submit" form="spend-cap-form" loading={savingCap}>
              Lưu
            </Button>
          </>
        }
      >
        <form id="spend-cap-form" onSubmit={handleSaveCap} className="space-y-4">
          <div className="rounded-md bg-gray-50 p-3 text-sm">
            <span className="text-gray-500">Tài khoản: </span>
            <span className="font-medium text-gray-900">{capTarget?.name ?? capTarget?.adAccountIdFb}</span>
          </div>
          <Input
            label="Giới hạn chi tiêu (VND) — để trống để bỏ giới hạn"
            type="number"
            min={0}
            value={capValue}
            onChange={(e) => setCapValue(e.target.value)}
            placeholder="Ví dụ: 2500000"
          />
          <p className="text-xs text-gray-500">
            Quảng cáo sẽ tạm dừng khi tổng chi tiêu chạm giới hạn. Giới hạn tính trên <strong>tổng tiêu
            trọn đời</strong> — nên đặt cao hơn "Tổng tiêu" hiện tại
            {capTarget?.amountSpent ? ` (${formatCurrency(capTarget.amountSpent)})` : ''}, nếu không tài
            khoản sẽ dừng ngay.
          </p>
        </form>
      </Modal>

      {/* Deploy-from-template modal — create a campaign into one/many ad accounts */}
      <Modal
        isOpen={deployOpen}
        onClose={() => setDeployOpen(false)}
        title="Tạo chiến dịch từ mẫu"
        footer={
          <>
            <Button variant="secondary" onClick={() => setDeployOpen(false)}>
              Hủy
            </Button>
            <Button type="submit" form="deploy-template-form" loading={deploying}>
              {deployActivate ? 'Tạo & Đăng' : 'Tạo (nháp)'}
            </Button>
          </>
        }
      >
        <form id="deploy-template-form" onSubmit={handleDeploy} className="space-y-4">
          <Select
            label="Mẫu"
            options={templateOptions}
            placeholder={loadingTemplates ? 'Đang tải mẫu…' : 'Chọn mẫu'}
            value={deployTemplateId}
            onChange={(e) => onPickTemplate(e.target.value)}
            required
          />
          {!loadingTemplates && templateOptions.length === 0 && (
            <p className="text-xs text-amber-600">
              Chưa có mẫu nào. Vào trang Chiến dịch, mở một chiến dịch chuẩn và bấm "☆ Đánh dấu mẫu" để thêm.
            </p>
          )}
          <MultiSelect
            label="Tài khoản đích (chọn 1 hoặc nhiều)"
            options={adAccountOptions}
            allLabel="Chọn tài khoản"
            value={deployAccounts}
            onChange={setDeployAccounts}
          />
          <div className="grid grid-cols-2 gap-3">
            <Input
              label={`Ngân sách/ngày — mỗi camp${
                deployCurrencies.length === 1 ? ` (${deployCurrencies[0]})` : ' (theo tiền tệ TK)'
              }`}
              type="number"
              min={0}
              value={deployBudget}
              onChange={(e) => setDeployBudget(e.target.value)}
              placeholder="Ví dụ: 50000"
              required
            />
            <Input
              label="Số bản / tài khoản"
              type="number"
              min={1}
              max={20}
              value={deployCopies}
              onChange={(e) => setDeployCopies(e.target.value)}
              placeholder="1"
            />
          </div>
          {deployAccounts.length > 0 && Number(deployBudget) > 0 && (
            <div className="space-y-1 text-xs text-gray-600">
              {(() => {
                const copies = Math.max(1, Math.floor(Number(deployCopies) || 1));
                const total = deployAccounts.length * copies;
                const ccy = deployCurrencies.length === 1 ? deployCurrencies[0] : null;
                const unit = ccy === 'VND' ? 'đ' : ccy ? ` ${ccy}` : '';
                return (
                  <p>
                    Sẽ tạo <span className="font-semibold">{total}</span> chiến dịch (
                    {deployAccounts.length} tài khoản × {copies} bản) · mỗi camp{' '}
                    <span className="font-semibold">
                      {Number(deployBudget).toLocaleString('vi-VN')}
                      {unit}
                    </span>
                    /ngày
                    {deployActivate ? ' — chạy thật ngay khi tạo.' : ' — lưu nháp (PAUSED).'}
                  </p>
                );
              })()}
              {deployCurrencies.length > 1 && (
                <p className="text-amber-600">
                  ⚠️ Bạn đang chọn nhiều tiền tệ ({deployCurrencies.join(', ')}). Số ngân sách nhập được áp
                  theo tiền tệ CỦA TỪNG tài khoản — nên deploy riêng từng loại (vd 50000 = 50.000đ với TK
                  VND nhưng = 50.000 USD với TK USD).
                </p>
              )}
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Tuổi từ (để trống = theo mẫu)"
              type="number"
              min={13}
              max={65}
              value={deployAgeMin}
              onChange={(e) => setDeployAgeMin(e.target.value)}
              placeholder="VD: 18"
            />
            <Input
              label="Tuổi đến"
              type="number"
              min={13}
              max={65}
              value={deployAgeMax}
              onChange={(e) => setDeployAgeMax(e.target.value)}
              placeholder="VD: 45"
            />
          </div>
          <Input
            label="Tên chiến dịch mới"
            value={deployName}
            onChange={(e) => setDeployName(e.target.value)}
            placeholder="Để trống để dùng tên mẫu"
          />
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
              checked={deployActivate}
              onChange={(e) => setDeployActivate(e.target.checked)}
            />
            Đăng ngay (ACTIVE) — bỏ chọn để tạo bản nháp (PAUSED)
          </label>
          <p className="text-xs text-gray-500">
            Tạo mới từ đầu (chiến dịch + nhóm QC + quảng cáo) giống mẫu 100%, chỉ đổi ngân sách. Cần tài
            khoản đích có quyền tạo + pixel/Trang được chia sẻ. Đây là thao tác live, tốn tiền thật khi
            đăng.
          </p>
        </form>
      </Modal>
    </div>
  );
}
